/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import Moment from "moment"; // Npm: moment.js library.
import _ from "underscore"; // Npm: underscore.js library.
import { useQuery } from "@apollo/client"; // Npm: Apollo client.
import { IoMdAdd } from "react-icons/io"; // Npm: React icons.
import { HiArrowPath } from "react-icons/hi2"; // Npm: React icons.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import TableSpinner from "components/TableSpinner";
import Modal from "components/Modal";
import ContentTranslationUpsert from "components/ContentTranslationUpsert";
import ContentTranslationDelete from "components/ContentTranslationDelete";
import ContentTranslationDropDownOptions from "components/ContentTranslationDropDownOptions";

/*
 * GRAPHS
 */
import ContentTranslationReadQuery from "./__query__/index.contentTranslation.read.query";

/*
 * STYLES
 */
import { buttonStyle, tabStyles } from "./index.style";

/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _Customers = React.useMemo(() => Object.React.App.enums.CONTENT_TRANSLATION_TYPE.enums.map(
    (i) => i.key,
  ), []);
  const _successFlags = React.useMemo(() =>
    Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
      (i) => i.key,
    ), []);

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });
  const [selectAll, setSelectAll] = React.useState(false);
  const [tabIndex, setTabIndex] = React.useState(0);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [filterModel, setFilterModel] = React.useState({
    items: [],
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });
  const _setFilterDebounced = React.useMemo(
    () => _.debounce(setFilterModel, 500),
    [],
  );
  React.useEffect(
    () => () => _setFilterDebounced.cancel(),
    [_setFilterDebounced],
  );
  const [selectedIds, setSelectedIds] = React.useState([]);
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);

  const _isFilterApplied = React.useMemo(
    () => filterModel?.items?.some((i) => i.value != void 0 && i.value !== ""),
    [filterModel],
  );
  const _appliedFilter = React.useMemo(
    () => _isFilterApplied ? filterModel : void 0,
    [_isFilterApplied, filterModel],
  );
  const _contentTranslationReadVariables = React.useMemo(() => ({
    type: _Customers[tabIndex],
    filter: _appliedFilter,
    skip: paginationModel.page * paginationModel.pageSize,
    take: paginationModel.pageSize,
  }), [_Customers, _appliedFilter, paginationModel.page, paginationModel.pageSize, tabIndex]);

  const _QueryContentTranslationRead = useQuery(ContentTranslationReadQuery, {
    variables: _contentTranslationReadVariables,
    fetchPolicy: _isPreviousPageRef.current ?
      "cache-first" :
      Object.React.App.fetchPolicy,
  });

  const {
    onOpen: onContentTranslationOpen,
    isOpen: isContentTranslationOpen,
    onClose: onContentTranslationClose,
  } = useDisclosure();
  const {
    isOpen: isContentTranslationUpdateOpen,
    onOpen: onContentTranslationUpdateOpen,
    onClose: onContentTranslationUpdateClose,
  } = useDisclosure();
  const {
    isOpen: isContentTranslationDeleteOpen,
    onOpen: onContentTranslationDeleteOpen,
    onClose: onContentTranslationDeleteClose,
  } = useDisclosure();
  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_QueryContentTranslationRead.data) {
      // Update states.
      setSelectedIds([]);
      setSelectAll(false);
    }
  }, [_QueryContentTranslationRead.data]);

  // Event handler.
  const _HandleSelectAll = React.useCallback(() => {
    // Const assignment.
    const _nextSelectAll = !selectAll;

    // Update states.
    setSelectAll(_nextSelectAll);
    setSelectedIds(
      _nextSelectAll
        ? _QueryContentTranslationRead?.data?.ContentTranslationRead?.map(
            (item) => item.id,
          ) || []
        : [],
    );
  }, [_QueryContentTranslationRead?.data?.ContentTranslationRead, selectAll]);
  const _HandleCheckboxChange = React.useCallback((id) => {
    // Check if id is already in selectedIds.
    setSelectedIds(j => {
      if (j.includes(id)) {
        // Remove id from selectedIds.
        return j.filter((selectedId) => selectedId !== id);
      }

      // Add id to selectedIds.
      return [...j, id];
    });
  }, []);
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false;
    setPaginationModel(j => {
      const nextPaginationModel = {
        ...j,
        page: 0,
      };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
  }, []);
  const _HandlePaginationModelChange = React.useCallback((__paginationModel) => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize;

    _paginationModelRef.current = __paginationModel;
    setPaginationModel(__paginationModel);
  }, []);
  const _HandleFilterModelChange = React.useCallback((__filterModel) => {
    _ResetPagination();
    _setFilterDebounced(__filterModel);
  }, [_ResetPagination, _setFilterDebounced]);
  const _HandleTabChange = React.useCallback((r) => {
    _ResetPagination();
    setTabIndex(r);
  }, [_ResetPagination]);
  const _HandleColumnVisibilityModelChange = React.useCallback((newModel) => {
    setColumnVisibilityModel(newModel);
  }, []);
  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _QueryContentTranslationRead.refetch();
    } finally {
      setIsRefreshing(false);
    }
  }, [_QueryContentTranslationRead]);
  const _contentTranslationReadRowCount = _.first(_QueryContentTranslationRead?.data?.ContentTranslationRead)?._totalCount ??
    _QueryContentTranslationRead?.data?.ContentTranslationRead?.length;
  const rowCount = React.useMemo(() => {
    if (undefined !== _contentTranslationReadRowCount && null !== _contentTranslationReadRowCount)
      _rowCountRef.current = _contentTranslationReadRowCount;

    return _rowCountRef.current;
  }, [_contentTranslationReadRowCount]);

  /*
   * DataGrid row selection needs current selectedIds for rendering.
   */
  const selectedIdsSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

  /*
   * Create stable selected id list for bulk action props.
   */
  const _GetContentTranslationIds = React.useCallback(rowId => _.uniq([...selectedIds, rowId]), [selectedIds]);

  /*
   * Keep table overlays stable across renders.
   */
  const slots = React.useMemo(() => ({
    loadingOverlay: () => (
      <TableSpinner
        isLoading={true}
        isEmpty={false}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
    noResultsOverlay: () => (
      <TableSpinner
        isLoading={false}
        isEmpty={true}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
    noRowsOverlay: () => (
      <TableSpinner
        isLoading={false}
        isEmpty={true}
        chopHeightFromHundredPercentage={
          _tableHeaderHeightRef?.current?.clientHeight
        }
      />
    ),
  }), []);

  // Define columns
  const columns = React.useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 60,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        renderHeader: () => (
          <Checkbox
            bg="gray.300"
            onChange={_HandleSelectAll}
            isChecked={selectAll}
          />
        ),
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Checkbox
              bg="gray.300"
              isChecked={selectedIdsSet.has(row.id)}
              onChange={() => _HandleCheckboxChange(row.id)}
            />
          </Box>
        ),
      },

      {
        field: "sno",
        headerName: "S No.",
        width: 80,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "id",
        headerName: "ID",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "CustomerAccount.displayName",
        headerName: "Customer Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "Mcc.countryName",
        headerName: "Country (Mcc)",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "regex",
        headerName: "Original Keyword",
        editable: false,
        sortable: true,
        filterable: true,
        // 'disableColumnMenu': true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "prefix",
        headerName: "Prefix",
        editable: false,
        sortable: true,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "suffix",
        headerName: "Suffix",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "omitWords",
        headerName: "OmitWords",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 150,
      },
      {
        field: "strategy",
        headerName: "Match Type",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "createdAt",
        headerName: "Created At",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 120,
      },
      {
        field: "action",
        headerName: "Actions",
        editable: false,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        width: 100,
        renderCell: ({ row }) => (
          <ContentTranslationDropDownOptions
            contentTranslationId={row?.id}
            onContentTranslationUpdate={onContentTranslationUpdateOpen}
            onContentTranslationDelete={onContentTranslationDeleteOpen}
            selectedIds={selectedIds}
            contentTranslationIds={_GetContentTranslationIds(row?.id)}
          />
        ),
      },
    ],
    [_GetContentTranslationIds, _HandleCheckboxChange, _HandleSelectAll, onContentTranslationDeleteOpen, onContentTranslationUpdateOpen, selectAll, selectedIds, selectedIdsSet],
  );

  // Define rows
  const rows = React.useMemo(
    () =>
      _.compact(
        _QueryContentTranslationRead?.data?.ContentTranslationRead?.map(
          (item, index) =>
            _successFlags.includes(item.status)
              ? {
                  id: item?.id ?? `content-translation-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                  sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                  "CustomerAccount.displayName":
                    item?.[
                      "TERMINATION" === _Customers[tabIndex]
                        ? "VendorAccount"
                        : "CustomerAccount"
                    ]?.displayName ?? "-",
                  "Mcc.countryName":
                    item?.Mcc?.mcc >= 0
                      ? `${item?.Mcc?.countryName} (${item?.Mcc?.mcc})`
                      : "-",
                  regex: _.isEmpty(item?.regex) ? "-" : item?.regex,
                  prefix: _.isEmpty(item?.prefix) ? "-" : item?.prefix,
                  suffix: _.isEmpty(item?.suffix) ? "-" : item?.suffix,
                  omitWords: _.isEmpty(item?.omitWords)
                    ? "-"
                    : item?.omitWords?.join(", "),
                  strategy: _.isEmpty(item?.strategy) ? "-" : item?.strategy,
                  createdAt: _.isEmpty(item.createdAt)
                    ? "-"
                    : Moment(item.createdAt).format("YYYY-MM-DD"),
                  updatedAt: _.isEmpty(item?.updatedAt)
                    ? "-"
                    : Moment(item?.updatedAt).format("YYYY-MM-DD"),
                }
              : null,
        ),
      ),
    [
      _QueryContentTranslationRead?.data?.ContentTranslationRead,
      _Customers,
      _successFlags,
      paginationModel.page,
      paginationModel.pageSize,
      tabIndex,
    ],
  );

  // Return component.
  return (
    <>
      <Flex
        className="contentTranslation base"
        minH="78vh"
        maxH="78vh"
        width="100%"
        overflow="hidden"
      >
        {/* inner container */}
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          height="100%"
          width="100%"
          overflow="hidden"
        >
          <Flex
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
            borderTopLeftRadius="20px"
            borderTopRadius="20px"
            p={_isCurrentViewMobile ? "12px" : "22px"}
            flexShrink={0}
            minH="auto"
          >
            <Tooltip label="Create new Translation." fontSize="sm">
              <Button
                w="auto"
                onClick={onContentTranslationOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                New Content Translation
              </Button>
            </Tooltip>
            <Tooltip label="Refresh translations." fontSize="sm">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
            </Tooltip>
          </Flex>

          {/* Tab and Table section */}
          <Tabs
            variant="enclosed"
            onChange={_HandleTabChange}
            display="flex"
            flexDirection="column"
            align="center"
            w="100%"
            flex={1}
            bg="white"
            isFitted
            overflow="hidden"
            minH={0}
          >
            <TabList
              bg="linear-Gradient(73deg,#390EF6 0%, rgba(146, 68, 244, 1) 100%)"
              overflowX={_isCurrentViewMobile ? "auto" : void 0}
              flexShrink={0}
              minH="auto"
            >
              {_Customers.map((u) => (
                <Tab
                  color="white"
                  textDecoration={"line-through"}
                  _focus={{ boxShadow: "none" }}
                  _selected={{ textDecoration: "none" }}
                  style={{ ...tabStyles, border: "none", borderRadius: 0 }}
                  p="1rem"
                  key={u}
                >
                  {u}
                </Tab>
              ))}
            </TabList>

            <Flex
              flex={1}
              flexDirection="column"
              border="2px solid #3A0FF6"
              borderTop="none"
              borderBottomRadius="15px"
              overflow="hidden"
              minH={0}
              width="100%"
            >
              {/* MUI DataGrid */}
              <DataGrid
                rows={rows}
                columns={columns}
                rowHeight={58}
                rowCount={rowCount}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={_HandlePaginationModelChange}
                filterMode="server"
                filterModel={filterModel}
                onFilterModelChange={_HandleFilterModelChange}
                pageSizeOptions={[10, 30, 50, 100]}
                slots={slots}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
                loading={_QueryContentTranslationRead?.loading}
                disableColumnMenu={false}
                sx={{
                  "& .MuiDataGrid-main": {
                    border: "none",
                    borderRadius: 0,
                  },
                }}
              />
            </Flex>
          </Tabs>
        </Flex>
        <Modal
          size="3xl"
          title="Create Translation"
          isOpen={isContentTranslationOpen}
          onClose={onContentTranslationClose}
          isCentered={false}
        >
          <ContentTranslationUpsert
            isCreateOnly={true}
            type={_Customers[tabIndex]}
            isVendor={"TERMINATION" === _Customers[tabIndex]}
            refetch={_QueryContentTranslationRead.refetch}
          />
        </Modal>
        <Modal
          size="3xl"
          title="Update Translation"
          isOpen={isContentTranslationUpdateOpen}
          onClose={onContentTranslationUpdateClose}
          isCentered={false}
        >
          <ContentTranslationUpsert
            isCreateOnly={false}
            type={_Customers[tabIndex]}
            isVendor={"TERMINATION" === _Customers[tabIndex]}
            refetch={_QueryContentTranslationRead.refetch}
          />
        </Modal>
        <Modal
          size="md"
          title="Translation Delete"
          isOpen={isContentTranslationDeleteOpen}
          onClose={onContentTranslationDeleteClose}
        >
          <ContentTranslationDelete refetch={_QueryContentTranslationRead.refetch} />
        </Modal>
      </Flex>
    </>
  );
};

/*
 * PROPTYPES
 */
Index.propTypes = {};

/*
 * EXPORT
 */
export default React.memo(Index);
